import React, { useEffect, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { clearCart } from '../redux/slices/cartSlice';
import { PageBackButton } from '../components';
import { useCheckoutForm } from '../hooks/useCheckoutForm';
import { useOrderCalculations } from '../hooks/useOrderCalculations';
import { useOrder } from '../hooks/useOrder';
import { useAppNavigate } from '../hooks/useAppNavigate';
import {
  CheckoutForm,
  OrderSummary,
  EmptyCart,
  PayLaterSuccessPopup,
  PayLaterFailurePopup,
} from '../components/checkout';

const CheckoutPage: React.FC = () => {
  const appNavigate = useAppNavigate();
  const { items: cartItems } = useAppSelector(state => state.cart);
  const websiteConfig = useAppSelector(state => state.siteContent.websiteConfig);
  const dispatch = useAppDispatch();
  
  // Custom hooks
  const {
    activeTab,
    formData,
    errors,
    handleChange,
    validateForm,
  } = useCheckoutForm();
  
  const { subtotal, deliveryFee, tax, total } = useOrderCalculations(cartItems, activeTab);
  
  // NEW: useOrder hook for pay now/pay later
  const {
    handleOrder,
    orderLoading,
    showSuccessPopup,
    showErrorPopup,
    handleErrorClose,
  } = useOrder();

  // Get payment preferences from Site Content API (websiteConfig.metadata)
  const { showPayNow, showPayLater } = useMemo(() => ({
    showPayNow: websiteConfig?.metadata?.paynow ?? true,
    showPayLater: websiteConfig?.metadata?.paylater ?? true,
  }), [websiteConfig?.metadata]);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      handleOrder(formData, total, activeTab, 'pay_now');
    }
  }, [validateForm, handleOrder, formData, total, activeTab]);

  const handleTakeoutSubmit = useCallback(() => {
    if (validateForm()) {
      handleOrder(formData, total, activeTab, 'pay_later');
    }
  }, [validateForm, handleOrder, formData, total, activeTab]);

  // Show empty cart if no items
  if (cartItems.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      {/* Checkout Content */}
      <div className="container mx-auto py-12 px-4">
        <PageBackButton text="Menu" onClick={() => appNavigate('/menu')} />
        <div className="mb-8">
          <h1 className="text-4xl font-bold">Checkout</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <CheckoutForm
              formData={formData}
              errors={errors}
              handleChange={handleChange}
              onSubmit={handleFormSubmit}
              onTakeoutOrder={handleTakeoutSubmit}
              orderLoading={orderLoading}
              cartItemsCount={cartItems.length}
              showPayNow={showPayNow}
              showPayLater={showPayLater}
              activeTab={activeTab}
            />
          </div>

          {/* Order Summary */}
          <div>
            <OrderSummary
              cartItems={cartItems}
              orderType={activeTab}
              subtotal={subtotal}
              deliveryFee={deliveryFee}
              tax={tax}
              total={total}
            />
          </div>
        </div>
      </div>

      {/* Pay Later Success Popup */}
      <PayLaterSuccessPopup
        isOpen={showSuccessPopup}
        onContinue={() => {
          dispatch(clearCart());
          appNavigate('/menu');
        }}
      />

      {/* Pay Later Failure Popup */}
      <PayLaterFailurePopup
        isOpen={showErrorPopup}
        onClose={handleErrorClose}
        onTryAgain={handleErrorClose}
      />
    </div>
  );
};

export default CheckoutPage;
